import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { 
  FiUser, 
  FiPhone, 
  FiMail, 
  FiMapPin,
  FiCalendar,
  FiBook,
  FiCheck,
  FiAward,
  FiPercent,
  FiGift,
  FiClock,
  FiMapPin as FiLocation,
  FiAlertCircle,
  FiShield,
  FiCheckCircle,
  FiSend
} from 'react-icons/fi';
import api from '../services/api';
import { useContent } from '../context/ContentContext';
import './ScholarshipPage.css';

const ScholarshipPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [exams, setExams] = useState([]);
  const [selectedExam, setSelectedExam] = useState(null);
  const [loadingExams, setLoadingExams] = useState(true);
  const { content } = useContent();
  
  // SMS Doğrulama State'leri
  const [verificationStep, setVerificationStep] = useState('form'); // 'form', 'code', 'verified'
  const [verificationToken, setVerificationToken] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [phoneForVerification, setPhoneForVerification] = useState('');
  
  const { register, handleSubmit, formState: { errors }, reset, setValue, watch } = useForm();
  const watchedPhone = watch('parentPhone');

  useEffect(() => {
    fetchActiveExams();
  }, []);

  const fetchActiveExams = async () => {
    try {
      const response = await api.get('/exams/active');
      const activeExams = response.data.filter(exam => exam.registrationStatus === 'open');
      setExams(activeExams);
      
      // Eğer sadece bir aktif sınav varsa otomatik seç
      if (activeExams.length === 1) {
        setSelectedExam(activeExams[0]);
        setValue('exam', activeExams[0]._id);
      }
    } catch (error) {
      console.error('Sınavlar yüklenemedi:', error);
      setExams([]);
    } finally {
      setLoadingExams(false);
    }
  };

  // Geri sayım için useEffect
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // SMS Doğrulama kodu gönder
  const sendVerificationCode = async () => {
    const phone = watchedPhone;
    if (!phone) {
      toast.error('Lütfen telefon numarası girin');
      return;
    }

    setSendingCode(true);
    try {
      const response = await api.post('/sms/send-verification', { phone });
      setPhoneForVerification(phone);
      setVerificationStep('code');
      setCountdown(120); // 2 dakika bekleme süresi
      toast.success('Doğrulama kodu gönderildi');
    } catch (error) {
      const message = error.response?.data?.message || 'SMS gönderilemedi';
      toast.error(message);
      if (error.response?.data?.waitSeconds) {
        setCountdown(error.response.data.waitSeconds);
      }
    } finally {
      setSendingCode(false);
    }
  };

  // Doğrulama kodunu kontrol et
  const verifyCode = async () => {
    if (verificationCode.length !== 6) {
      toast.error('6 haneli doğrulama kodunu girin');
      return;
    }

    setVerifyingCode(true);
    try {
      const response = await api.post('/sms/verify-code', {
        phone: phoneForVerification,
        code: verificationCode
      });
      setVerificationToken(response.data.verificationToken);
      setVerificationStep('verified');
      toast.success('Telefon numarası doğrulandı!');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Doğrulama başarısız');
    } finally {
      setVerifyingCode(false);
    }
  };

  const onSubmit = async (data) => {
    if (!selectedExam) {
      toast.error('Lütfen bir sınav seçin');
      return;
    }

    // SMS doğrulaması kontrolü
    if (verificationStep !== 'verified' || !verificationToken) {
      toast.error('Lütfen önce telefon numaranızı doğrulayın');
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post('/applications', {
        ...data,
        exam: selectedExam._id,
        verificationToken
      });
      setIsSuccess(true);
      reset();
      setVerificationStep('form');
      setVerificationToken(null);
      setVerificationCode('');
      toast.success('Başvurunuz başarıyla alındı!');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Bir hata oluştu');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // İkon eşleştirme
  const iconMap = {
    percent: FiPercent,
    award: FiAward,
    gift: FiGift
  };

  // Varsayılan benefits
  const defaultBenefits = [
    { icon: 'percent', title: '%100\'e Varan Burs', desc: 'Başarınıza göre tam burs imkanı' },
    { icon: 'award', title: 'Ücretsiz Deneme', desc: 'Sınavda başarılı olun, ücretsiz deneyin' },
    { icon: 'gift', title: 'Özel Hediyeler', desc: 'Başarılı öğrencilere sürpriz hediyeler' }
  ];

  const benefits = (content.scholarship?.benefits || defaultBenefits).map(b => ({
    ...b,
    IconComponent: iconMap[b.icon] || FiAward
  }));

  if (isSuccess) {
    return (
      <div className="scholarship-page">
        <div className="success-container">
          <motion.div 
            className="success-content"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="success-icon">
              <FiCheck />
            </div>
            <h2>Başvurunuz Alındı!</h2>
            <p>
              <strong>{selectedExam?.title}</strong> sınavı için başvurunuz başarıyla kaydedildi.
              <br />
              Sınav Tarihi: <strong>{formatDate(selectedExam?.examDate)}</strong>
              <br />
              En kısa sürede sizinle iletişime geçeceğiz.
            </p>
            <button className="btn btn-primary" onClick={() => { setIsSuccess(false); setSelectedExam(null); }}>
              Yeni Başvuru
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  // Sınav seçimi yoksa veya aktif sınav yoksa
  const noExamsAvailable = !loadingExams && exams.length === 0;

  return (
    <div className="scholarship-page">
      {/* Hero */}
      <section className="scholarship-hero">
        <div className="scholarship-hero-bg"></div>
        <div className="container">
          <motion.div 
            className="scholarship-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1>Bursluluk Sınavı Başvurusu</h1>
            <p>%100'e varan burs imkanı ile hayalinizdeki eğitime kavuşun</p>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="benefits-section section" style={{ background: 'var(--gray-50)' }}>
        <div className="container">
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <motion.div 
                key={index}
                className="benefit-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="benefit-icon">
                  <benefit.IconComponent />
                </div>
                <h3>{benefit.title}</h3>
                <p>{benefit.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Exam Selection & Form Section */}
      <section className="form-section section">
        <div className="container">
          {loadingExams ? (
            <div className="exams-loading">
              <div className="spinner"></div>
              <p>Sınavlar yükleniyor...</p>
            </div>
          ) : noExamsAvailable ? (
            <div className="no-exams">
              <FiAlertCircle />
              <h2>Şu anda açık başvuru bulunmuyor</h2>
              <p>Yakında yeni bursluluk sınavları duyurulacaktır. Takipte kalın!</p>
              <a href={`tel:${content.contact?.phone || '02121234567'}`} className="btn btn-primary">
                <FiPhone /> Bilgi Almak İçin Arayın
              </a>
            </div>
          ) : (
            <>
              {/* Exam Selection */}
              {!selectedExam && exams.length > 1 && (
                <motion.div 
                  className="exam-selection"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <h2>Sınav Seçin</h2>
                  <p>Başvurmak istediğiniz bursluluk sınavını seçin</p>
                  
                  <div className="exam-cards">
                    {exams.map((exam, index) => (
                      <motion.div
                        key={exam._id}
                        className="exam-selection-card"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        onClick={() => { setSelectedExam(exam); setValue('exam', exam._id); }}
                      >
                        <div className="exam-selection-header">
                          <h3>{exam.title}</h3>
                          <span className="exam-badge">Başvuru Açık</span>
                        </div>
                        <div className="exam-selection-body">
                          <div className="exam-info-row">
                            <FiCalendar />
                            <span>Sınav: {formatDate(exam.examDate)}</span>
                          </div>
                          <div className="exam-info-row">
                            <FiClock />
                            <span>Saat: {exam.examTime}</span>
                          </div>
                          <div className="exam-info-row">
                            <FiLocation />
                            <span>{exam.location}</span>
                          </div>
                          <div className="exam-info-row">
                            <FiCalendar />
                            <span>Son Başvuru: {formatDate(exam.applicationEndDate)}</span>
                          </div>
                        </div>
                        <button className="btn btn-primary btn-block">
                          Bu Sınava Başvur
                        </button>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Application Form - Show when exam is selected */}
              {selectedExam && (
                <div className="form-grid">
                  {/* Form */}
                  <motion.div 
                    className="form-container"
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                  >
                    {/* Selected Exam Info */}
                    <div className="selected-exam-banner">
                      <div className="selected-exam-info">
                        <h3>{selectedExam.title}</h3>
                        <p>
                          <FiCalendar /> {formatDate(selectedExam.examDate)} - {selectedExam.examTime}
                          <span className="divider">|</span>
                          <FiLocation /> {selectedExam.location}
                        </p>
                      </div>
                      {exams.length > 1 && (
                        <button 
                          className="btn btn-secondary btn-sm"
                          onClick={() => setSelectedExam(null)}
                        >
                          Sınav Değiştir
                        </button>
                      )}
                    </div>

                    <h2>Başvuru Formu</h2>
                    <p className="form-subtitle">Lütfen tüm alanları eksiksiz doldurunuz.</p>

                    <form onSubmit={handleSubmit(onSubmit)}>
                      {/* Öğrenci Bilgileri */}
                      <div className="form-section-title">
                        <FiUser />
                        <span>Öğrenci Bilgileri</span>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label className="form-label">Öğrenci Adı *</label>
                          <input
                            type="text"
                            className={`form-input ${errors.studentName ? 'error' : ''}`}
                            placeholder="Adı"
                            {...register('studentName', { required: 'Bu alan zorunludur' })}
                          />
                          {errors.studentName && <span className="form-error">{errors.studentName.message}</span>}
                        </div>
                        <div className="form-group">
                          <label className="form-label">Öğrenci Soyadı *</label>
                          <input
                            type="text"
                            className={`form-input ${errors.studentSurname ? 'error' : ''}`}
                            placeholder="Soyadı"
                            {...register('studentSurname', { required: 'Bu alan zorunludur' })}
                          />
                          {errors.studentSurname && <span className="form-error">{errors.studentSurname.message}</span>}
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label className="form-label">Doğum Tarihi *</label>
                          <input
                            type="date"
                            className={`form-input ${errors.birthDate ? 'error' : ''}`}
                            {...register('birthDate', { required: 'Bu alan zorunludur' })}
                          />
                          {errors.birthDate && <span className="form-error">{errors.birthDate.message}</span>}
                        </div>
                        <div className="form-group">
                          <label className="form-label">TC Kimlik No *</label>
                          <input
                            type="text"
                            className={`form-input ${errors.tcNo ? 'error' : ''}`}
                            placeholder="11 haneli TC No"
                            maxLength={11}
                            {...register('tcNo', { 
                              required: 'Bu alan zorunludur',
                              pattern: { value: /^\d{11}$/, message: '11 haneli rakam olmalıdır' }
                            })}
                          />
                          {errors.tcNo && <span className="form-error">{errors.tcNo.message}</span>}
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label className="form-label">Mevcut Okul *</label>
                          <input
                            type="text"
                            className={`form-input ${errors.currentSchool ? 'error' : ''}`}
                            placeholder="Okul adı"
                            {...register('currentSchool', { required: 'Bu alan zorunludur' })}
                          />
                          {errors.currentSchool && <span className="form-error">{errors.currentSchool.message}</span>}
                        </div>
                        <div className="form-group">
                          <label className="form-label">Sınıf *</label>
                          <select
                            className={`form-select ${errors.currentGrade ? 'error' : ''}`}
                            {...register('currentGrade', { required: 'Bu alan zorunludur' })}
                          >
                            <option value="">Sınıf Seçin</option>
                            {selectedExam.targetGrades?.map(grade => (
                              <option key={grade} value={grade}>{grade}. Sınıf</option>
                            ))}
                          </select>
                          {errors.currentGrade && <span className="form-error">{errors.currentGrade.message}</span>}
                        </div>
                      </div>

                      {/* Veli Bilgileri */}
                      <div className="form-section-title">
                        <FiPhone />
                        <span>Veli Bilgileri</span>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Veli Adı Soyadı *</label>
                        <input
                          type="text"
                          className={`form-input ${errors.parentName ? 'error' : ''}`}
                          placeholder="Veli adı soyadı"
                          {...register('parentName', { required: 'Bu alan zorunludur' })}
                        />
                        {errors.parentName && <span className="form-error">{errors.parentName.message}</span>}
                      </div>

                      {/* Telefon ve SMS Doğrulama */}
                      <div className="phone-verification-section">
                        <div className="form-row">
                          <div className="form-group">
                            <label className="form-label">Telefon *</label>
                            <div className="phone-input-wrapper">
                              <input
                                type="tel"
                                className={`form-input ${errors.parentPhone ? 'error' : ''}`}
                                placeholder="05XX XXX XX XX"
                                disabled={verificationStep === 'verified'}
                                {...register('parentPhone', { 
                                  required: 'Bu alan zorunludur',
                                  pattern: { 
                                    value: /^(05|5)\d{9}$/,
                                    message: 'Geçerli bir telefon numarası girin'
                                  }
                                })}
                              />
                              {verificationStep === 'verified' && (
                                <span className="verified-badge">
                                  <FiCheckCircle /> Doğrulandı
                                </span>
                              )}
                            </div>
                            {errors.parentPhone && <span className="form-error">{errors.parentPhone.message}</span>}
                          </div>
                          <div className="form-group">
                            <label className="form-label">E-posta *</label>
                            <input
                              type="email"
                              className={`form-input ${errors.parentEmail ? 'error' : ''}`}
                              placeholder="ornek@email.com"
                              {...register('parentEmail', { 
                                required: 'Bu alan zorunludur',
                                pattern: { value: /^\S+@\S+$/i, message: 'Geçerli bir email girin' }
                              })}
                            />
                            {errors.parentEmail && <span className="form-error">{errors.parentEmail.message}</span>}
                          </div>
                        </div>

                        {/* SMS Doğrulama Alanı */}
                        {verificationStep !== 'verified' && (
                          <div className="sms-verification-box">
                            <div className="sms-verification-header">
                              <FiShield />
                              <span>SMS Doğrulaması</span>
                            </div>
                            
                            {verificationStep === 'form' && (
                              <div className="sms-verification-content">
                                <p>Başvurunuzu tamamlamak için telefon numaranızı doğrulamanız gerekmektedir.</p>
                                <button
                                  type="button"
                                  className="btn btn-secondary"
                                  onClick={sendVerificationCode}
                                  disabled={sendingCode || countdown > 0 || !watchedPhone}
                                >
                                  {sendingCode ? (
                                    <>
                                      <span className="spinner" style={{ width: 16, height: 16 }}></span>
                                      Gönderiliyor...
                                    </>
                                  ) : countdown > 0 ? (
                                    `${countdown} saniye bekleyin`
                                  ) : (
                                    <>
                                      <FiSend /> Doğrulama Kodu Gönder
                                    </>
                                  )}
                                </button>
                              </div>
                            )}

                            {verificationStep === 'code' && (
                              <div className="sms-verification-content">
                                <p>
                                  <strong>{phoneForVerification}</strong> numarasına gönderilen 6 haneli kodu girin.
                                </p>
                                <div className="verification-code-input">
                                  <input
                                    type="text"
                                    maxLength={6}
                                    placeholder="000000"
                                    value={verificationCode}
                                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                                    className="form-input code-input"
                                  />
                                  <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={verifyCode}
                                    disabled={verifyingCode || verificationCode.length !== 6}
                                  >
                                    {verifyingCode ? (
                                      <span className="spinner" style={{ width: 16, height: 16 }}></span>
                                    ) : (
                                      'Doğrula'
                                    )}
                                  </button>
                                </div>
                                <div className="resend-code">
                                  {countdown > 0 ? (
                                    <span>Yeni kod için {countdown} saniye bekleyin</span>
                                  ) : (
                                    <button
                                      type="button"
                                      className="btn-link"
                                      onClick={sendVerificationCode}
                                      disabled={sendingCode}
                                    >
                                      Tekrar Kod Gönder
                                    </button>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {verificationStep === 'verified' && (
                          <div className="sms-verified-box">
                            <FiCheckCircle />
                            <span>Telefon numaranız doğrulandı</span>
                          </div>
                        )}
                      </div>

                      <div className="form-group">
                        <label className="form-label">Meslek</label>
                        <input
                          type="text"
                          className="form-input"
                          placeholder="Veli mesleği"
                          {...register('parentJob')}
                        />
                      </div>

                      {/* Adres Bilgileri */}
                      <div className="form-section-title">
                        <FiMapPin />
                        <span>Adres Bilgileri</span>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label className="form-label">İl *</label>
                          <input
                            type="text"
                            className={`form-input ${errors.city ? 'error' : ''}`}
                            placeholder="İl"
                            {...register('city', { required: 'Bu alan zorunludur' })}
                          />
                          {errors.city && <span className="form-error">{errors.city.message}</span>}
                        </div>
                        <div className="form-group">
                          <label className="form-label">İlçe *</label>
                          <input
                            type="text"
                            className={`form-input ${errors.district ? 'error' : ''}`}
                            placeholder="İlçe"
                            {...register('district', { required: 'Bu alan zorunludur' })}
                          />
                          {errors.district && <span className="form-error">{errors.district.message}</span>}
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Adres *</label>
                        <textarea
                          className={`form-textarea ${errors.address ? 'error' : ''}`}
                          placeholder="Açık adres"
                          rows={3}
                          {...register('address', { required: 'Bu alan zorunludur' })}
                        ></textarea>
                        {errors.address && <span className="form-error">{errors.address.message}</span>}
                      </div>

                      {/* Ek Bilgiler */}
                      <div className="form-section-title">
                        <FiBook />
                        <span>Ek Bilgiler</span>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Bizi Nereden Duydunuz?</label>
                        <select className="form-select" {...register('howDidYouHear')}>
                          <option value="">Seçin</option>
                          <option value="internet">İnternet</option>
                          <option value="friend">Arkadaş/Tanıdık</option>
                          <option value="advertisement">Reklam</option>
                          <option value="social_media">Sosyal Medya</option>
                          <option value="other">Diğer</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Eklemek İstedikleriniz</label>
                        <textarea
                          className="form-textarea"
                          placeholder="Varsa eklemek istediğiniz notlar..."
                          rows={3}
                          {...register('notes')}
                        ></textarea>
                      </div>

                      {/* KVKK */}
                      <div className="form-group">
                        <label className="form-checkbox">
                          <input
                            type="checkbox"
                            {...register('kvkkApproval', { required: 'KVKK onayı zorunludur' })}
                          />
                          <span>
                            Kişisel verilerimin işlenmesine ilişkin{' '}
                            <a href="/kvkk" target="_blank" rel="noopener noreferrer">
                              KVKK Aydınlatma Metni
                            </a>
                            'ni okudum ve kabul ediyorum. *
                          </span>
                        </label>
                        {errors.kvkkApproval && <span className="form-error">{errors.kvkkApproval.message}</span>}
                      </div>

                      <button 
                        type="submit" 
                        className="btn btn-primary btn-lg submit-btn"
                        disabled={isSubmitting || verificationStep !== 'verified'}
                      >
                        {isSubmitting ? (
                          <>
                            <span className="spinner" style={{ width: 20, height: 20 }}></span>
                            Gönderiliyor...
                          </>
                        ) : verificationStep !== 'verified' ? (
                          <>
                            <FiShield /> Önce Telefonu Doğrulayın
                          </>
                        ) : (
                          <>
                            Başvuruyu Gönder
                            <FiCheck />
                          </>
                        )}
                      </button>
                    </form>
                  </motion.div>

                  {/* Sidebar */}
                  <motion.div 
                    className="form-sidebar"
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                  >
                    <div className="sidebar-card">
                      <h3>
                        <FiCalendar />
                        Sınav Bilgileri
                      </h3>
                      <ul className="exam-info-list">
                        <li>
                          <span className="label">Sınav Adı</span>
                          <span className="value">{selectedExam.title}</span>
                        </li>
                        <li>
                          <span className="label">Tarih</span>
                          <span className="value">{formatDate(selectedExam.examDate)}</span>
                        </li>
                        <li>
                          <span className="label">Saat</span>
                          <span className="value">{selectedExam.examTime}</span>
                        </li>
                        <li>
                          <span className="label">Yer</span>
                          <span className="value">{selectedExam.location}</span>
                        </li>
                        <li>
                          <span className="label">Son Başvuru</span>
                          <span className="value">{formatDate(selectedExam.applicationEndDate)}</span>
                        </li>
                      </ul>
                    </div>

                    {selectedExam.scholarshipRates && selectedExam.scholarshipRates.length > 0 && (
                      <div className="sidebar-card">
                        <h3>
                          <FiAward />
                          Burs Oranları
                        </h3>
                        <ul className="scholarship-rates">
                          {selectedExam.scholarshipRates.map((rate, index) => (
                            <li key={index}>
                              <span>{rate.rank}. Sıra</span>
                              <span className="rate">%{rate.rate} Burs</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="sidebar-card contact-card">
                      <h3>Sorularınız mı var?</h3>
                      <p>Bursluluk sınavı hakkında detaylı bilgi için bizi arayın.</p>
                      <a href={`tel:${content.contact?.phone || '02121234567'}`} className="btn btn-primary">
                        <FiPhone />
                        {content.contact?.phone || '0212 123 45 67'}
                      </a>
                    </div>
                  </motion.div>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default ScholarshipPage;
